import React, {useState, useEffect} from 'react';
import {useUnit} from 'effector-react';
import {Drawer} from '../../../core/components/drawer/Drawer';
import {SvgIcon} from '../../../core/components/svgIcon/SvgIcon';
import {Tooltip} from '../../../core/components/tooltip/Tooltip';
import {
  $countriesTerms,
  $sectorsTerms,
  $programmeTerms,
  $organizationCategoriesTerms,
  $organizationEmployeesNumberTerms,
  fetchTermsRequested,
  $expertiseTerms,
} from '../../../core/store/terms';
import {Term} from '@/core/store/terms/types';
import {PartnerFilters} from '../model/types';
import {fetchPartnersRequested, $partnersStore} from '../store';
import './FiltersDrawer.scss';
import {imageList} from '@/core/utils/imageList';
import {PartnersFilter} from '../pages/PartnersPage';

export interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onFiltersApplied?: (filters: FilterState) => void;
  type: PartnersFilter;
  appliedFilters?: {
    countries: string[];
    sectors: string[];
    programmes: string[];
    organizationCategories: string[];
    companySize: string[];
    legalForm: string[];
    expertise: string[];
  };
}

interface FilterState {
  countries: string[];
  sectors: string[];
  programmes: string[];
  expertise: string[];
  organizationCategories: string[];
  companySize: string[];
  legalForm: string[];
  countrySearch: string;
  sectorSearch: string;
  programmeSearch: string;
  expertiseSearch: string;
}

const initialFilterState: FilterState = {
  countries: [],
  sectors: [],
  programmes: [],
  expertise: [],
  organizationCategories: [],
  companySize: [],
  legalForm: [],
  countrySearch: '',
  sectorSearch: '',
  programmeSearch: '',
  expertiseSearch: '',
};

// Helper functions for term data
const getTopLevelTerms = (terms: Term[]): Term[] => {
  return terms.filter((term) => term.depth === 0 || term.parent === '0');
};

const getChildTerms = (terms: Term[], parentTid: string): Term[] => {
  return terms.filter((term) => term.parent === parentTid);
};

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  isOpen,
  onClose,
  onFiltersApplied,
  appliedFilters,
  type,
}) => {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [showMoreCountries, setShowMoreCountries] = useState(false);
  const [showMoreSectors, setShowMoreSectors] = useState(false);
  const [showMoreProgrammes, setShowMoreProgrammes] = useState(false);
  const [showMoreExpertise, setShowMoreExpertise] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState<string[]>([]);

  // Get terms data from store
  const [
    countriesTerms,
    sectorsTerms,
    programmeTerms,
    expertiseTerms,
    organizationCategoriesTerms,
    organizationEmployeesNumberTerms,
    fetchTerms,
    partnersState,
  ] = useUnit([
    $countriesTerms,
    $sectorsTerms,
    $programmeTerms,
    $expertiseTerms,
    $organizationCategoriesTerms,
    $organizationEmployeesNumberTerms,
    fetchTermsRequested,
    $partnersStore,
  ]);

  // Get sectors data for hierarchical structure
  const sectorsData = sectorsTerms.data || [];

  // Fetch terms data when component mounts
  useEffect(() => {
    if (isOpen) {
      fetchTerms({vocId: 'countries'});
      fetchTerms({vocId: 'sectors'});
      fetchTerms({vocId: 'programme'});
      fetchTerms({vocId: 'expertise'});
      fetchTerms({vocId: 'organization_categories'});
      fetchTerms({vocId: 'organization_employees_number'});
    }
  }, [isOpen, fetchTerms]);

  // Initialize filters when drawer opens or appliedFilters change
  useEffect(() => {
    if (isOpen) {
      const initialFilters = appliedFilters || initialFilterState;
      setFilters({
        ...initialFilters,
        countrySearch: '',
        sectorSearch: '',
        programmeSearch: '',
        expertiseSearch: '',
      });

      // Expand sectors that have selected children
      const selectedSectors = initialFilters.sectors;
      const sectorsToExpand: string[] = [];

      selectedSectors.forEach((selectedSectorTid) => {
        // Check if this is a child sector
        const parentSector = sectorsData.find((sector) =>
          getChildTerms(sectorsData, sector.tid).some((child) => child.tid === selectedSectorTid),
        );
        if (parentSector && !sectorsToExpand.includes(parentSector.tid)) {
          sectorsToExpand.push(parentSector.tid);
        }
      });

      setExpandedSectors(sectorsToExpand);
    }
  }, [isOpen, appliedFilters, sectorsData]);

  const handleCheckboxChange = (category: keyof FilterState, value: string) => {
    if (Array.isArray(filters[category])) {
      const currentValues = filters[category] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      setFilters({...filters, [category]: newValues});
    }
  };

  const handleSectorToggle = (sectorTid: string) => {
    setExpandedSectors((prev) =>
      prev.includes(sectorTid) ? prev.filter((s) => s !== sectorTid) : [...prev, sectorTid],
    );
  };

  // Filter countries data
  const filteredCountries = countriesTerms.data.filter((country) =>
    country.name.toLowerCase().includes(filters.countrySearch.toLowerCase()),
  );

  // Create hierarchical structure for sectors
  const topLevelSectors = getTopLevelTerms(sectorsData);
  const filteredSectors = topLevelSectors.filter((sector) =>
    sector.name.toLowerCase().includes(filters.sectorSearch.toLowerCase()),
  );

  const filteredProgrammes = programmeTerms.data.filter((programme) =>
    programme.name.toLowerCase().includes(filters.programmeSearch.toLowerCase()),
  );
  const filteredExpertise = expertiseTerms.data.filter((expertise) =>
    expertise.name.toLowerCase().includes(filters.expertiseSearch.toLowerCase()),
  );

  // Organization categories and employee numbers (no search needed)
  const organizationCategories = organizationCategoriesTerms.data;
  const companySizeOptions = organizationEmployeesNumberTerms.data;

  const handleClearAll = () => {
    // Clear local filter state
    setFilters(initialFilterState);
    setExpandedSectors([]);
    setShowMoreCountries(false);
    setShowMoreSectors(false);
    setShowMoreProgrammes(false);

    // Clear filters from store and refresh partners
    fetchPartnersRequested({});

    // Call the onFiltersApplied callback with empty filters to update parent component
    onFiltersApplied?.(initialFilterState);
    // onClose();
  };

  const handleApply = () => {
    // Helper function to format filters for API
    const formatFiltersForAPI = (filterState: FilterState): PartnerFilters => {
      const apiFilters: PartnerFilters = {
        // Preserve existing filters from the store
        ...partnersState.filters,
      };

      // Add new filter values only if they have selections
      if (filterState.countries.length > 0) {
        apiFilters.countries = filterState.countries.join(',');
      }
      if (filterState.sectors.length > 0) {
        apiFilters.sectors = filterState.sectors.join(',');
      }
      if (filterState.programmes.length > 0) {
        apiFilters.programmes = filterState.programmes.join(',');
      }
      if (filterState.expertise.length > 0) {
        apiFilters.expertise = filterState.expertise.join(',');
      }
      if (filterState.organizationCategories.length > 0) {
        apiFilters.organization_categories = filterState.organizationCategories.join(',');
      }
      if (filterState.companySize.length > 0) {
        apiFilters.company_size = filterState.companySize.join(',');
      }
      if (filterState.legalForm.length > 0) {
        apiFilters.legal_form = filterState.legalForm.join(',');
      }

      // Remove undefined values
      const cleanFilters = Object.fromEntries(
        Object.entries(apiFilters).filter(([_, value]) => value !== undefined && value !== ''),
      ) as PartnerFilters;

      return cleanFilters;
    };

    // Call the original onFiltersApplied callback - parent will handle the API calls
    onFiltersApplied?.(filters);
    onClose();
  };
  const legalFormTypes = [
    {label: 'Private', value: '0'},
    {label: 'Public', value: '1'},
  ];
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Filters" width="400px">
      {type === 'all' && (
        <div className="filters-drawer">
          <p className="filters-description">Apply filters to partners.</p>

          {/* Countries */}
          <div className="filter-section">
            <h3>
              Country{' '}
              <Tooltip text="Filter partners by their country location" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Countries"
                value={filters.countrySearch}
                onChange={(e) => setFilters({...filters, countrySearch: e.target.value})}
              />
            </div>
            {countriesTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredCountries.slice(0, showMoreCountries ? filteredCountries.length : 5).map((country) => (
                  <label key={country.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.countries.includes(country.tid)}
                      onChange={() => handleCheckboxChange('countries', country.tid)}
                    />
                    <span className="checkmark"></span>
                    {country.name}
                  </label>
                ))}
                {filteredCountries.length > 5 && (
                  <button className="show-more-btn" onClick={() => setShowMoreCountries(!showMoreCountries)}>
                    Show {showMoreCountries ? 'less' : `${filteredCountries.length - 5} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Organization Categories */}
          <div className="filter-section">
            <h3>
              Organization Type{' '}
              <Tooltip text="Filter by the type of organization or entity" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            {organizationCategoriesTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {organizationCategories.map((category) => (
                  <label key={category.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.organizationCategories.includes(category.tid)}
                      onChange={() => handleCheckboxChange('organizationCategories', category.tid)}
                    />
                    <span className="checkmark"></span>
                    {category.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Legal form */}
          <div className="filter-section">
            <h3>
              Legal form{' '}
              <Tooltip text="Filter partners by their legal form type" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="checkbox-list">
              {legalFormTypes.map((legalForm) => (
                <label key={legalForm.value} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.legalForm.includes(legalForm.value)}
                    onChange={() => handleCheckboxChange('legalForm', legalForm.value)}
                  />
                  <span className="checkmark"></span>
                  {legalForm.label}
                </label>
              ))}
            </div>
          </div>

          {/* Company Size */}
          <div className="filter-section">
            <h3>
              Organization Size{' '}
              <Tooltip text="Filter partners by their organization size based on number of employees" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            {organizationEmployeesNumberTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {companySizeOptions.map((size) => (
                  <label key={size.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.companySize.includes(size.tid)}
                      onChange={() => handleCheckboxChange('companySize', size.tid)}
                    />
                    <span className="checkmark"></span>
                    {size.name}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Sectors */}
          <div className="filter-section">
            <h3>
              Sectors{' '}
              <Tooltip text="Filter partners by their sectors of activity or expertise" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Sectors"
                value={filters.sectorSearch}
                onChange={(e) => setFilters({...filters, sectorSearch: e.target.value})}
              />
            </div>
            {sectorsTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredSectors.map((sector) => {
                  const childSectors = getChildTerms(sectorsData, sector.tid);
                  const hasChildren = childSectors.length > 0;

                  return (
                    <div key={sector.tid}>
                      <div className="checkbox-item expandable">
                        {hasChildren && (
                          <button className="expand-btn" onClick={() => handleSectorToggle(sector.tid)}>
                            <SvgIcon
                              icon={expandedSectors.includes(sector.tid) ? 'icon-chevron-down' : 'icon-chevron-right'}
                            />
                          </button>
                        )}
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.sectors.includes(sector.tid)}
                            onChange={() => handleCheckboxChange('sectors', sector.tid)}
                          />
                          <span className="checkmark"></span>
                          {sector.name}
                        </label>
                      </div>
                      {hasChildren && expandedSectors.includes(sector.tid) && (
                        <div className="subsector-list">
                          {childSectors.map((subsector) => (
                            <label key={subsector.tid} className="checkbox-item subsector">
                              <input
                                type="checkbox"
                                checked={filters.sectors.includes(subsector.tid)}
                                onChange={() => handleCheckboxChange('sectors', subsector.tid)}
                              />
                              <span className="checkmark"></span>
                              {subsector.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Programmes */}
          <div className="filter-section">
            <h3>
              Programmes{' '}
              <Tooltip text="Filter partners by the funding programmes they participate in" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Programmes"
                value={filters.programmeSearch}
                onChange={(e) => setFilters({...filters, programmeSearch: e.target.value})}
              />
            </div>
            {programmeTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredProgrammes.slice(0, showMoreProgrammes ? filteredProgrammes.length : 5).map((programme) => (
                  <label key={programme.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.programmes.includes(programme.tid)}
                      onChange={() => handleCheckboxChange('programmes', programme.tid)}
                    />
                    <span className="checkmark"></span>
                    {programme.name}
                  </label>
                ))}
                {filteredProgrammes.length > 5 && (
                  <button className="show-more-btn" onClick={() => setShowMoreProgrammes(!showMoreProgrammes)}>
                    Show {showMoreProgrammes ? 'less' : `${filteredProgrammes.length - 5} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="filter-actions">
            <button className="clear-btn" onClick={handleClearAll}>
              Clear all
            </button>
            <div className="action-buttons">
              <button className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button className="apply-btn" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      {type === 'my_network' && (
        <div className="filters-drawer">
          <p className="filters-description">Apply filters to partners.</p>

          {/* Expertise */}
          <div className="filter-section">
            <h3>
              Field of expertise{' '}
              <Tooltip text="Filter members by expertise" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Expertise"
                value={filters.expertiseSearch}
                onChange={(e) => setFilters({...filters, expertiseSearch: e.target.value})}
              />
            </div>
            {expertiseTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredExpertise.slice(0, showMoreExpertise ? filteredExpertise.length : 5).map((expertise) => (
                  <label key={expertise.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.expertise.includes(expertise.tid)}
                      onChange={() => handleCheckboxChange('expertise', expertise.tid)}
                    />
                    <span className="checkmark"></span>
                    {expertise.name}
                  </label>
                ))}
                {filteredExpertise.length > 5 && (
                  <button className="show-more-btn" onClick={() => setShowMoreExpertise(!showMoreExpertise)}>
                    Show {showMoreExpertise ? 'less' : `${filteredExpertise.length - 5} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sectors */}
          <div className="filter-section">
            <h3>
              Sectors{' '}
              <Tooltip text="Filter partners by their sectors of activity or expertise" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Sectors"
                value={filters.sectorSearch}
                onChange={(e) => setFilters({...filters, sectorSearch: e.target.value})}
              />
            </div>
            {sectorsTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredSectors.map((sector) => {
                  const childSectors = getChildTerms(sectorsData, sector.tid);
                  const hasChildren = childSectors.length > 0;

                  return (
                    <div key={sector.tid}>
                      <div className="checkbox-item expandable">
                        {hasChildren && (
                          <button className="expand-btn" onClick={() => handleSectorToggle(sector.tid)}>
                            <SvgIcon
                              icon={expandedSectors.includes(sector.tid) ? 'icon-chevron-down' : 'icon-chevron-right'}
                            />
                          </button>
                        )}
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.sectors.includes(sector.tid)}
                            onChange={() => handleCheckboxChange('sectors', sector.tid)}
                          />
                          <span className="checkmark"></span>
                          {sector.name}
                        </label>
                      </div>
                      {hasChildren && expandedSectors.includes(sector.tid) && (
                        <div className="subsector-list">
                          {childSectors.map((subsector) => (
                            <label key={subsector.tid} className="checkbox-item subsector">
                              <input
                                type="checkbox"
                                checked={filters.sectors.includes(subsector.tid)}
                                onChange={() => handleCheckboxChange('sectors', subsector.tid)}
                              />
                              <span className="checkmark"></span>
                              {subsector.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Countries */}
          <div className="filter-section">
            <h3>
              Country{' '}
              <Tooltip text="Filter partners by their country location" position="top">
                <img src={imageList.helpIcon} />
              </Tooltip>
            </h3>
            <div className="search-wrapper">
              <SvgIcon icon="icon-filter" />
              <input
                type="text"
                placeholder="Search Countries"
                value={filters.countrySearch}
                onChange={(e) => setFilters({...filters, countrySearch: e.target.value})}
              />
            </div>
            {countriesTerms.loading ? (
              <div className="loading-state">Loading...</div>
            ) : (
              <div className="checkbox-list">
                {filteredCountries.slice(0, showMoreCountries ? filteredCountries.length : 5).map((country) => (
                  <label key={country.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.countries.includes(country.tid)}
                      onChange={() => handleCheckboxChange('countries', country.tid)}
                    />
                    <span className="checkmark"></span>
                    {country.name}
                  </label>
                ))}
                {filteredCountries.length > 5 && (
                  <button className="show-more-btn" onClick={() => setShowMoreCountries(!showMoreCountries)}>
                    Show {showMoreCountries ? 'less' : `${filteredCountries.length - 5} more`}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="filter-actions">
            <button className="clear-btn" onClick={handleClearAll}>
              Clear all
            </button>
            <div className="action-buttons">
              <button className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button className="apply-btn" onClick={handleApply}>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};
